import { useState, useEffect, useRef, RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import theme from '@whitelabel/global-theme';
import { FORM_INPUT_TYPES } from './constants';
import { GOOGLE_PLACES_API_KEY } from './api';
import { IXCMSImage } from './types';
import { smoothScrollToAnchor } from './url';
import { captureExceptionWithFullyStory } from './utils';

export const useQueryParams = (): URLSearchParams => new URLSearchParams(useLocation().search);

export function useGoogleAPI(input?: any) {
  const [googleAPILoaded, setGoogleAPILoaded] = useState(window.google);

  useEffect(() => {
    const scriptID = 'google-maps-places-api';
    const existingScript = document.getElementById(scriptID);

    if (!existingScript) {
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
      (window as any).mapsCallback = () => setGoogleAPILoaded(true);
      const script = document.createElement('script');
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}` +
        '&libraries=places&callback=mapsCallback';
      script.id = scriptID;
      if (input !== FORM_INPUT_TYPES.ADDRESS) {
        script.defer = true;
      }
      document.head.appendChild(script);
    }
  }, [input]);
  return googleAPILoaded;
}

export function useClientRect(element: RefObject<HTMLElement>): DOMRectReadOnly {
  const [elementRect, setElementRect] = useState<DOMRectReadOnly>();
  const observer = useRef(
    new ResizeObserver((entries) => {
      setElementRect(entries[0].contentRect);
    }),
  );

  useEffect(() => {
    if (!element.current) return;
    if (element.current) {
      observer.current.observe(element.current);
    }
    return () => {
      if (!element.current) return;
      observer.current.unobserve(element.current);
    };
  }, [element, observer]);

  return elementRect as DOMRectReadOnly;
}

// Ref: https://usehooks.com/useScript/
export function useScript(src: any, id: any, removeScript = false) {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = useState(src ? 'loading' : 'idle');

  useEffect(
    () => {
      // Allow falsy src value if waiting on other data needed for
      // constructing the script URL passed to this hook.
      if (!src) {
        setStatus('idle');
        return;
      }

      // Fetch existing script element by src
      // It may have been added by another instance of this hook
      let script: HTMLScriptElement | undefined = document.querySelector(`script[src="${src}"]`) as
        | HTMLScriptElement
        | undefined;
      // removing the script from the body
      if (script && removeScript) {
        script?.parentElement?.removeChild(script);
      }
      if (!script || (script && removeScript)) {
        // Create script
        script = document.createElement('script');

        script.src = src;
        if (id) {
          script.id = id;
        }
        script.async = true;
        script.setAttribute('data-status', 'loading');
        // Add script to document body
        document.body.appendChild(script);

        // Store status in attribute on script
        // This can be read by other instances of this hook
        const setAttributeFromEvent = (event: any) => {
          script!.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
        };

        script.addEventListener('load', setAttributeFromEvent);
        script.addEventListener('error', setAttributeFromEvent);
      } else {
        // Grab existing script status from attribute and set to state.
        setStatus(script.getAttribute('data-status')!);
      }

      // Script event handler to update status in state
      // Note: Even if the script already exists we still need to add
      // event handlers to update the state for *this* hook instance.
      const setStateFromEvent = (event: any) => {
        setStatus(event.type === 'load' ? 'ready' : 'error');
      };

      // Add event listeners
      script.addEventListener('load', setStateFromEvent);
      script.addEventListener('error', setStateFromEvent);

      // Remove event listeners on cleanup
      return () => {
        if (script) {
          script.removeEventListener('load', setStateFromEvent);
          script.removeEventListener('error', setStateFromEvent);
        }
      };
    },
    [src], // Only re-run effect if script src changes
  );

  return status;
}
interface IUsePollingArgs {
  callback: () => Promise<any>;
  onSuccess: (result: any) => void;
  onFailure: () => void;
  getIsFinished: (result: any) => boolean;
  delay: number;
  maxRetries: number;
  startDelay?: number;
}

export function usePolling({
  callback,
  onSuccess,
  onFailure,
  getIsFinished,
  delay,
  maxRetries,
  startDelay = 0,
}: IUsePollingArgs): void {
  useEffect(() => {
    let counter = 0;
    let cancelled = false;

    async function poll() {
      let isFinished;
      let result;
      try {
        result = await callback();
        isFinished = getIsFinished(result);
      } catch {
        isFinished = false;
      }

      if (isFinished) {
        onSuccess(result);
      } else if (!isFinished && counter < maxRetries) {
        counter += 1;
        setTimeout(() => {
          if (!cancelled) poll();
        }, delay);
      } else {
        onFailure();
      }
    }

    if (startDelay) {
      setTimeout(() => {
        if (!cancelled) poll();
      }, startDelay);
    } else {
      poll();
    }

    return () => {
      cancelled = true;
    };
    // Empty deps array to have the code run only once.
  }, []);
}

export const usePageViews = (): void => {
  const location = useLocation();
  useEffect(() => {
    if (window.isHeadless) {
      return;
    }
    window.bwtag('set', 'appId', process.env.REACT_APP_BRIGHTWRITE_APP_ID);
    window.bwtag('set', 'appVersion', process.env.REACT_APP_BRIGHTWRITE_APP_VERSION);
  }, []);
  useEffect(() => {
    if (window.isHeadless) {
      return;
    }
    window.bwtag('pv');
  }, [location]);
};

interface IResponsiveBackgroundImageProps {
  backgroundImage?: IXCMSImage;
}

export const useResponsiveBackgroundImage = ({
  backgroundImage,
}: IResponsiveBackgroundImageProps): string | undefined => {
  const isMobile = !useMediaQuery({ minWidth: theme.gridBreakpoints.sm });
  const isTablet = useMediaQuery({ minWidth: theme.gridBreakpoints.sm, maxWidth: theme.gridBreakpoints.lg });

  if (isMobile && backgroundImage?.fileForMobile) {
    return backgroundImage.fileForMobile;
  }
  if (isTablet && backgroundImage?.fileForTablet) {
    return backgroundImage.fileForTablet;
  }
  return backgroundImage?.file;
};

export const useScrollToAnchor = (anchor?: string, gap?: number): void => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === `#${anchor}`) {
      smoothScrollToAnchor(`#${anchor}`, gap)();
    }
  }, [anchor, gap, location.hash]);
};

export const useFramerMotion = () => {
  const [framerMotionExports, setFramerMotionExports] = useState<any>({});
  const loadFramerMotion = async () => {
    try {
      const data = await import('framer-motion');
      setFramerMotionExports(data);
    } catch (error) {
      captureExceptionWithFullyStory(error);
    }
  };

  useEffect(() => {
    loadFramerMotion();
  }, []);

  return framerMotionExports;
};
